import { useState } from "react";
import { NoRealData } from "@/components/shared";
import { METODOLOGIAS_CARBONO_REFERENCIA, TIPOS_PROJETO_CARBONO } from "@/config/ambiental-carbono-config";
import MethodologyCard from "./components/MethodologyCard";

const tabs = [
  { id: "overview", label: "Visão Geral" },
  { id: "projects", label: "Projetos de Carbono" },
  { id: "trees", label: "Estimativas por Exemplar" },
  { id: "areas", label: "Áreas e UCs" },
  { id: "compensations", label: "Compensações" },
  { id: "methodologies", label: "Metodologias" },
  { id: "reports", label: "Relatórios" },
];

const EMPTY: Record<string, { title: string; description: string; icon: string }> = {
  overview: {
    title: "Sem estimativa de carbono calculada",
    description:
      "A visão geral mostrará CO₂e, carbono e biomassa estimados a partir das medições dendrométricas das vistorias validadas. Ainda não há cálculo real de carbono disponível nesta tela.",
    icon: "ri-cloud-line",
  },
  projects: {
    title: "Nenhum projeto de carbono com dado real",
    description:
      "Aqui serão listados os projetos com estimativa de carbono, exemplares considerados e nível de confiança. Ainda não há fonte real de dado para esta lista.",
    icon: "ri-folder-chart-line",
  },
  trees: {
    title: "Sem estimativas por exemplar arbóreo",
    description:
      "Aqui aparecerá a estimativa de biomassa e carbono de cada exemplar, com a metodologia aplicada. Ainda não há cálculo real por exemplar.",
    icon: "ri-plant-line",
  },
  areas: {
    title: "Sem estimativas por área verde ou UC",
    description:
      "Depende do cadastro de áreas verdes e Unidades de Conservação, que ainda não existe com dado real.",
    icon: "ri-landscape-line",
  },
  compensations: {
    title: "Nenhuma compensação ambiental registrada",
    description:
      "Aqui serão acompanhadas as compensações (mudas exigidas, plantadas e sobrevivência). Ainda não há fonte real de dado para esta lista.",
    icon: "ri-seedling-line",
  },
  reports: {
    title: "Nenhum relatório de carbono disponível",
    description: "Os relatórios técnicos serão gerados quando houver estimativa de carbono calculada com dado real.",
    icon: "ri-file-chart-line",
  },
};

export default function Carbono() {
  const [activeTab, setActiveTab] = useState("overview");
  const empty = EMPTY[activeTab];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Carbono</div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
          Carbono e Ativos Ambientais
        </h1>
        <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
          Estimativas preliminares de biomassa, carbono, CO₂e e ativos ambientais vinculados ao CTA, áreas verdes e projetos urbanos.
        </p>
      </div>

      {/* Technical Warning */}
      <div className="bg-caution-50 border border-caution-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-caution-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-caution-700 w-full h-full flex items-center justify-center text-lg" />
          </div>
          <div>
            <div className="text-sm font-semibold text-caution-800 mb-1">Aviso técnico — estimativas preliminares</div>
            <p className="text-xs text-caution-700 leading-relaxed">
              Quando disponíveis, os valores serão estimativas preliminares baseadas em dados dendrométricos, espécies cadastradas e modelos simplificados. Não representam certificação oficial de crédito de carbono. As metodologias devem ser calibradas por responsável técnico conforme espécie, região e objetivo do projeto.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px] space-y-5">
        {activeTab === "methodologies" ? (
          <>
            <div className="bg-accent-50 border border-accent-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <i className="ri-book-open-line text-accent-600 w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
                <p className="text-xs text-accent-700 leading-relaxed">
                  Metodologias de referência. Nenhuma está aplicada a cálculos do FloraIQ ainda; devem ser calibradas por responsável técnico conforme espécie, região, literatura científica e objetivo do projeto.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {METODOLOGIAS_CARBONO_REFERENCIA.map((m) => (
                <MethodologyCard key={m.id} methodology={m} />
              ))}
            </div>
          </>
        ) : (
          <>
            <NoRealData variant="page" icon={empty.icon} title={empty.title} description={empty.description} />
            {activeTab === "projects" && (
              <div className="bg-white rounded-card border border-background-200 p-4">
                <div className="text-xs font-semibold text-foreground-700 mb-2">Tipos de projeto previstos</div>
                <div className="flex flex-wrap gap-1.5">
                  {TIPOS_PROJETO_CARBONO.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 bg-background-50 text-[11px] text-foreground-600 rounded-md border border-background-200"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
